import { useState } from 'react';
import { useReglages } from '../api/reglages.jsx';
import { iaApi } from '../api/resources';
import { messageIa } from '../lib/ia';
import { CLE_PACK_IA, packIaActif } from '../lib/pack-ia';
import { C } from '../theme/palette';

/** État pendant l'appel : le bouton ne se relance pas. */
const EN_COURS = 'en cours';

/**
 * « ✨ Rédiger » : une proposition de texte, que l'utilisateur relit.
 *
 * Le bouton pré-remplit le champ qu'il accompagne ; il ne l'enregistre pas et
 * ne remplace rien sans geste de l'utilisateur, qui garde la main. Il ne
 * paraît que si la société a souscrit le Pack IA : une fonction qu'on ne peut
 * pas utiliser n'a pas à être montrée.
 *
 * @param {object} props propriétés du composant
 * @param {'devis'|'courrier'} props.genre ligne de devis ou paragraphe de courrier
 * @param {string} [props.titre] désignation de la ligne ou objet du courrier
 * @param {string} [props.mots] mots-clés ou notes déjà saisis
 * @param {(texte: string) => void} props.onTexte reçoit la proposition
 * @param {string} [props.color] couleur d'accent
 * @param {boolean} [props.full] occupe toute la largeur
 * @returns {import('react').ReactElement|null} le bouton, ou rien sans le pack
 */
export function BoutonRediger({ genre, titre, mots, onTexte, color = C.purple, full }) {
  const { reglageJson } = useReglages();
  const actif = packIaActif(reglageJson(CLE_PACK_IA, null));
  const [etat, setEtat] = useState('');
  const [note, setNote] = useState('');
  if (!actif) return null;

  const redige = async () => {
    setEtat(EN_COURS);
    setNote('');
    try {
      const { texte } = await iaApi.redaction({
        genre,
        titre: String(titre || '').slice(0, 200),
        mots: String(mots || '').slice(0, 1500),
      });
      onTexte(texte);
      setEtat('');
      setNote('Proposition insérée : relis-la et corrige ce qui ne te convient pas.');
    } catch (e) {
      setEtat(messageIa(e));
    }
  };
  const enCours = etat === EN_COURS;

  return (
    <div style={{ marginBottom: 8 }}>
      <button
        type="button"
        onClick={redige}
        disabled={enCours}
        aria-label="Rédiger avec le Pack IA"
        style={{
          background: enCours ? C.border : color,
          color: '#fff',
          border: 'none',
          borderRadius: 9,
          padding: '9px 13px',
          fontSize: 10.5,
          fontWeight: 700,
          cursor: enCours ? 'wait' : 'pointer',
          width: full ? '100%' : 'auto',
        }}
      >
        {enCours ? '✨ Rédaction en cours…' : '✨ Rédiger'}
      </button>
      {etat && !enCours && (
        <div role="alert" style={{ fontSize: 9.5, color: C.red, marginTop: 5, lineHeight: 1.5 }}>
          {etat}
        </div>
      )}
      {note && (
        <div style={{ fontSize: 9, color: C.muted, marginTop: 5, lineHeight: 1.5 }}>{note}</div>
      )}
    </div>
  );
}
